import * as React from "react"

import useReadAloudPlayer from '../../../Shared/hooks/readAloud/useReadAloudPlayer'
import { KaraokeChunk, ReadAloudPlayerBar } from '../../../Shared/index'
import { CLIP_SOURCE_TYPE, clipSource } from '../../../Shared/libs/speech/clipSource'
import { collectHtmlWords } from '../../../Shared/libs/speech/speechText'
import { speechTextFromWords } from '../../../Shared/libs/speech/wordCollector'
import { addPTagsToPassages } from '../../helpers/containerActionHelpers'

const BOLD_ELEMENTS = 'strong, b'

const isHeading = (paragraph: Element) => {
  const unbolded = paragraph.cloneNode(true) as Element
  unbolded.querySelectorAll(BOLD_ELEMENTS).forEach(element => element.remove())
  return !unbolded.textContent?.trim()
}

const passageParagraphs = (passages) => {
  if (!passages[0]?.text) { return [] }

  const [html] = addPTagsToPassages(passages.slice(0, 1), true)
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  const source = clipSource(CLIP_SOURCE_TYPE.EVIDENCE_PASSAGE, passages[0].id)
  return Array.from(parsed.querySelectorAll('p')).map(paragraph => ({ heading: isHeading(paragraph), html: paragraph.innerHTML, source, }))
}

export const passageChunks = (passages, title, titleSource = null) => [{ html: title || '', source: titleSource, }, ...passageParagraphs(passages)]

export const passageChunkRequests = (passages, title, titleSource) => passageChunks(passages, title, titleSource).map(chunk => ({ source: chunk.source, text: speechTextFromWords(collectHtmlWords(chunk.html)), }))

const ReadAloudPassage = ({ controlsId, headerImage, onExit, passages, renderActionsBar, title, }) => {
  const chunks = React.useMemo(() => passageChunks(passages, title), [passages, title])

  const { activeChunkIndex, activeWordIndex, cycleRate, pause, play, prepared, rate, rewind, status, stop, } = useReadAloudPlayer({ chunks, })
  const autoPlayedRef = React.useRef(false)

  React.useEffect(() => {
    if (!prepared || autoPlayedRef.current) { return }

    autoPlayedRef.current = true
    play()
  }, [prepared])

  const handleExit = () => {
    stop()
    onExit()
  }

  const playerBar = (
    <ReadAloudPlayerBar
      controlsId={controlsId}
      cycleRate={cycleRate}
      onExit={handleExit}
      pause={pause}
      play={play}
      rate={rate}
      rewind={rewind}
      status={status}
    />
  )

  return (
    <div className="read-aloud-passage">
      <p className="sr-only">Read aloud is on. Passage highlighting is paused until you stop listening.</p>
      <KaraokeChunk
        className={activeChunkIndex === 0 ? 'title karaoke-active-chunk' : 'title'}
        element="h1"
        html={chunks[0].html}
        spokenWordIndex={activeChunkIndex === 0 ? activeWordIndex : null}
      />
      {headerImage}
      {renderActionsBar(playerBar)}
      <div className="passage">
        {chunks.slice(1).map((chunk, i) => {
          const chunkIndex = i + 1
          const isActive = chunkIndex === activeChunkIndex
          return (
            <KaraokeChunk
              className={isActive ? 'karaoke-active-chunk' : undefined}
              html={chunk.html}
              key={chunkIndex}
              spokenWordIndex={isActive ? activeWordIndex : null}
            />
          )
        })}
        <span aria-hidden="true" id="end-of-passage" />
      </div>
    </div>
  )
}

export default ReadAloudPassage
